import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NiceSelect from "../Header/NiceSelect";
import apiService, { handleApiError } from "../../services/api";

function Booking() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        serviceType: "",
        // Common fields
        adults: 1,
        children: 0,
        // Air Ticket fields
        fromDestination: "",
        toDestination: "",
        departureDate: "",
        returnDate: "",
        tripType: "return", // one-way or return
        // Hotel fields
        hotel: "",
        checkIn: "",
        checkOut: "",
        // Car fields
        carType: "",
        pickupDate: "",
        returnCarDate: "",
        pickupLocation: "",
        // Package fields
        destination: "",
        packageType: "",
        packageCheckIn: "",
        packageCheckOut: "",
        // Airbnb fields
        accommodationLocation: "",
        accommodationType: "",
        airbnbCheckIn: "",
        airbnbCheckOut: "",
        guests: 2
    });

    const [formMessage, setFormMessage] = useState({ text: "", type: "" });
    const [loading, setLoading] = useState(true);

    // Dynamic data from API
    const [serviceTypes, setServiceTypes] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [carTypes, setCarTypes] = useState([]);
    const [packageTypes, setPackageTypes] = useState([]);
    // Airbnb related state
    const [accommodationLocations, setAccommodationLocations] = useState([]);
    const [accommodationTypes, setAccommodationTypes] = useState([]);

    // Static options
    const tripTypeOptions = [
        { value: "one-way", label: "One Way" },
        { value: "return", label: "Return" },
    ];

    // Fetch all booking form data on component mount
    useEffect(() => {
        const fetchBookingData = async () => {
            try {
                setLoading(true);
                
                // Fetch all data in parallel
                const [serviceTypesRes, destinationsRes, hotelsRes, carTypesRes, packageTypesRes, accommodationLocationsRes, accommodationTypesRes] = await Promise.all([
                    apiService.getServiceTypes(),
                    apiService.getDestinations(),
                    apiService.getAccommodationLocations(),
                    apiService.getCarTypes(),
                    apiService.getPackageTypes(),
                    apiService.getAccommodationLocations(),
                    apiService.getAccommodationTypes()
                ]);

                // Map service types
                const serviceTypeOptions = serviceTypesRes.results?.map(st => ({
                    value: st.name,
                    label: st.name
                })) || [];
                console.log('Service types loaded:', serviceTypeOptions);
                setServiceTypes(serviceTypeOptions);

                // Map destinations
                const destinationOptions = destinationsRes.results?.map(dest => ({
                    value: dest.name,
                    label: `${dest.name}, ${dest.country}`
                })) || [];
                console.log('Destinations loaded at', new Date().toLocaleTimeString(), ':', destinationOptions);
                setDestinations(destinationOptions);

                // Map hotels
                const hotelOptions = hotelsRes.results?.map(hotel => ({
                    value: hotel.name,
                    label: `${hotel.name} - ${hotel.location}${hotel.star_rating ? ` (${hotel.star_rating}⭐)` : ''}`
                })) || [];
                setHotels(hotelOptions);

                // Map car types
                const carTypeOptions = carTypesRes.results?.map(car => ({
                    value: car.name.toLowerCase(),
                    label: `${car.name} (${car.passenger_capacity} passengers)`
                })) || [];
                setCarTypes(carTypeOptions);

                // Map package types
                const packageTypeOptions = packageTypesRes.results?.map(pkg => ({
                    value: pkg.name.toLowerCase().replace(' ', '-'),
                    label: pkg.name
                })) || [];
                setPackageTypes(packageTypeOptions);

                // Map accommodation locations for Airbnb
                const accommodationLocationOptions = accommodationLocationsRes.results?.map(location => ({
                    value: location.name,
                    label: `${location.name}, ${location.country || location.city || ''}`
                })) || [];
                console.log('Accommodation locations loaded:', accommodationLocationOptions);
                setAccommodationLocations(accommodationLocationOptions);

                // Map accommodation types for Airbnb
                const accommodationTypeOptions = accommodationTypesRes.results?.map(type => ({
                    value: type.name,
                    label: type.name
                })) || [];
                console.log('Accommodation types loaded:', accommodationTypeOptions);
                setAccommodationTypes(accommodationTypeOptions);

            } catch (error) {
                console.error('Error fetching booking data:', error);
                console.error('Error details:', error.response || error.message);
                setFormMessage({ 
                    text: 'Failed to load booking options. Using defaults.', 
                    type: 'error' 
                });
                
                // Fallback to default options if API fails
                setServiceTypes([
                    { value: "Package", label: "Package" },
                    { value: "Hotel", label: "Hotel" },
                    { value: "Air Ticket", label: "Air Ticket" },
                    { value: "Car", label: "Car" },
                    { value: "AirBnB", label: "AirBnB" },
                ]);
                setDestinations([
                    { value: "Australia", label: "Australia" },
                    { value: "Dubai", label: "Dubai" },
                    { value: "England", label: "England" },
                    { value: "Thailand", label: "Thailand" },
                ]);
                setHotels([
                    { value: "Luxury Resort", label: "Luxury Resort" },
                    { value: "Beach Hotel", label: "Beach Hotel" },
                ]);
                setCarTypes([
                    { value: "economy", label: "Economy" },
                    { value: "compact", label: "Compact" },
                    { value: "suv", label: "SUV" },
                    { value: "luxury", label: "Luxury" },
                ]);
                setPackageTypes([
                    { value: "honeymoon-package", label: "Honeymoon Package" },
                    { value: "family-package", label: "Family Package" },
                    { value: "adventure-package", label: "Adventure Package" },
                ]);
                setAccommodationLocations([
                    { value: "Dar es Salaam", label: "Dar es Salaam, Tanzania" },
                    { value: "Zanzibar", label: "Zanzibar, Tanzania" },
                    { value: "Arusha", label: "Arusha, Tanzania" },
                    { value: "Moshi", label: "Moshi, Tanzania" },
                ]);
                setAccommodationTypes([
                    { value: "Entire Apartment", label: "Entire Apartment" },
                    { value: "Private Room", label: "Private Room" },
                    { value: "Shared Room", label: "Shared Room" },
                    { value: "Villa", label: "Villa" },
                    { value: "House", label: "House" },
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchBookingData();
    }, []);

    // Handle input field changes
    const handleChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle date changes for check-in/check-out
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle number inputs (adults, children)
    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: parseInt(value) || 0
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate required fields based on service type
        if (!formData.serviceType) {
            setFormMessage({ text: "Please select a service type.", type: "error" });
            return;
        }

        // Different validation based on service type
        if (formData.serviceType === "Package") {
            if (!formData.destination || !formData.packageType || !formData.packageCheckIn || !formData.packageCheckOut) {
                setFormMessage({ text: "Please fill in all required package fields.", type: "error" });
                return;
            }
        } else if (formData.serviceType === "Hotel") {
            if (!formData.destination || !formData.hotel || !formData.checkIn || !formData.checkOut) {
                setFormMessage({ text: "Please fill in all required hotel fields.", type: "error" });
                return;
            }
        } else if (formData.serviceType === "Air Ticket") {
            if (!formData.fromDestination || !formData.toDestination || !formData.departureDate) {
                setFormMessage({ text: "Please fill in departure city, arrival city, and departure date.", type: "error" });
                return;
            }
            if (formData.tripType === "return" && !formData.returnDate) {
                setFormMessage({ text: "Please select a return date for round trip.", type: "error" });
                return;
            }
        } else if (formData.serviceType === "Car") {
            if (!formData.pickupLocation || !formData.carType || !formData.pickupDate || !formData.returnCarDate) {
                setFormMessage({ text: "Please fill in all required car rental fields.", type: "error" });
                return;
            }
        } else if (formData.serviceType === "AirBnB" || formData.serviceType === "Airbnb") {
            if (!formData.accommodationLocation || !formData.accommodationType || !formData.airbnbCheckIn || !formData.airbnbCheckOut) {
                setFormMessage({ text: "Please fill in all required Airbnb fields.", type: "error" });
                return;
            }
        }

        // Log form data for debugging
        console.log('Form submitted with data:', formData);

        // Handle Air Ticket search - redirect to Agency page
        if (formData.serviceType === "Air Ticket") {
            // Build query parameters for flight search
            const searchParams = new URLSearchParams({
                from: formData.fromDestination || '',
                to: formData.toDestination || '',
                departure: formData.departureDate || '',
                return: formData.returnDate || '',
                tripType: formData.tripType || 'return',
                adults: formData.adults || 1,
                children: formData.children || 0
            });

            // Redirect to Agency page with search parameters
            navigate(`/home-agency?${searchParams.toString()}`);
            return;
        }

        // Handle Airbnb search - redirect to Airbnb page
        if (formData.serviceType === "AirBnB" || formData.serviceType === "Airbnb") {
            // Build query parameters for search
            const searchParams = new URLSearchParams({
                location: formData.accommodationLocation || '',
                type: formData.accommodationType || '',
                checkin: formData.airbnbCheckIn || '',
                checkout: formData.airbnbCheckOut || '',
                guests: formData.guests || 2,
                adults: formData.adults || 1,
                children: formData.children || 0
            });

            // Redirect to Airbnb/yacht page with search parameters
            navigate(`/home-yacht?${searchParams.toString()}`);
            return;
        }

        // Form successfully submitted for other service types
        setFormMessage({ 
            text: `${formData.serviceType === "Air Ticket" ? "Flight booking" : (formData.serviceType + " search")} request submitted successfully!`, 
            type: "success" 
        });

        // Reset form for non-Airbnb searches
        setFormData({
            serviceType: "",
            adults: 1,
            children: 0,
            fromDestination: "",
            toDestination: "",
            departureDate: "",
            returnDate: "",
            tripType: "return",
            hotel: "",
            checkIn: "",
            checkOut: "",
            carType: "",
            pickupDate: "",
            returnCarDate: "",
            pickupLocation: "",
            destination: "",
            packageType: "",
            packageCheckIn: "",
            packageCheckOut: "",
            accommodationLocation: "",
            accommodationType: "",
            airbnbCheckIn: "",
            airbnbCheckOut: "",
            guests: 2
        });
    };

    return (
        <div className="booking-sec">
            <div className="container">
                <form onSubmit={handleSubmit} className="booking-form">
                    <div className="input-wrap">
                        <div className="row align-items-center justify-content-between">
                            {/* Service Type Selection - Always visible */}
                            <div className="form-group col-md-6 col-lg-auto">
                                <div className="icon">
                                    <i className="fa-light fa-briefcase" />
                                </div>
                                <div className="search-input">
                                    <label>Service Type</label>
                                    <NiceSelect
                                        options={serviceTypes}
                                        defaultValue="Select Service"
                                        onChange={(value) => handleChange("serviceType", value)}
                                    />
                                </div>
                            </div>

                            {/* PACKAGE SERVICE TYPE FIELDS */}
                            {formData.serviceType === "Package" && (
                                <>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-route" />
                                        </div>
                                        <div className="search-input">
                                            <label>Destination</label>
                                            <NiceSelect
                                                options={destinations}
                                                defaultValue="Select Destination"
                                                onChange={(value) => handleChange("destination", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-box" />
                                        </div>
                                        <div className="search-input">
                                            <label>Package Type</label>
                                            <NiceSelect
                                                options={packageTypes}
                                                defaultValue="Select Package"
                                                onChange={(value) => handleChange("packageType", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Check In</label>
                                            <input 
                                                type="date" 
                                                name="packageCheckIn" 
                                                value={formData.packageCheckIn} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Check Out</label>
                                            <input 
                                                type="date" 
                                                name="packageCheckOut" 
                                                value={formData.packageCheckOut} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* HOTEL SERVICE TYPE FIELDS */}
                            {formData.serviceType === "Hotel" && (
                                <>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-route" />
                                        </div>
                                        <div className="search-input">
                                            <label>Location</label>
                                            <NiceSelect
                                                options={destinations}
                                                defaultValue="Select Location"
                                                onChange={(value) => handleChange("destination", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-hotel" />
                                        </div>
                                        <div className="search-input">
                                            <label>Hotel</label>
                                            <NiceSelect
                                                options={hotels}
                                                defaultValue="Select Hotel"
                                                onChange={(value) => handleChange("hotel", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Check In</label>
                                            <input 
                                                type="date" 
                                                name="checkIn" 
                                                value={formData.checkIn} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Check Out</label>
                                            <input 
                                                type="date" 
                                                name="checkOut" 
                                                value={formData.checkOut} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* AIR TICKET SERVICE TYPE FIELDS */}
                            {formData.serviceType === "Air Ticket" && (
                                <>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-plane-departure" />
                                        </div>
                                        <div className="search-input">
                                            <label>From</label>
                                            <NiceSelect
                                                options={destinations}
                                                defaultValue="Departure City"
                                                onChange={(value) => handleChange("fromDestination", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-plane-arrival" />
                                        </div>
                                        <div className="search-input">
                                            <label>To</label>
                                            <NiceSelect
                                                options={destinations}
                                                defaultValue="Arrival City"
                                                onChange={(value) => handleChange("toDestination", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-route" />
                                        </div>
                                        <div className="search-input">
                                            <label>Trip Type</label>
                                            <NiceSelect
                                                options={tripTypeOptions}
                                                defaultValue="Return"
                                                onChange={(value) => handleChange("tripType", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Departure Date</label>
                                            <input 
                                                type="date" 
                                                name="departureDate" 
                                                value={formData.departureDate} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                    {formData.tripType === "return" && (
                                        <div className="form-group col-md-6 col-lg-auto">
                                            <div className="icon">
                                                <i className="fa-light fa-calendar-days" />
                                            </div>
                                            <div className="search-input">
                                                <label>Return Date</label>
                                                <input 
                                                    type="date" 
                                                    name="returnDate" 
                                                    value={formData.returnDate} 
                                                    onChange={handleDateChange}
                                                    className="form-control" 
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* CAR SERVICE TYPE FIELDS */}
                            {formData.serviceType === "Car" && (
                                <>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-map-location-dot" />
                                        </div>
                                        <div className="search-input">
                                            <label>Pickup Location</label>
                                            <NiceSelect
                                                options={destinations}
                                                defaultValue="Select Location"
                                                onChange={(value) => handleChange("pickupLocation", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-car" />
                                        </div>
                                        <div className="search-input">
                                            <label>Car Type</label>
                                            <NiceSelect
                                                options={carTypes}
                                                defaultValue="Select Car Type"
                                                onChange={(value) => handleChange("carType", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Pickup Date</label>
                                            <input 
                                                type="date" 
                                                name="pickupDate" 
                                                value={formData.pickupDate} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Return Date</label>
                                            <input 
                                                type="date" 
                                                name="returnCarDate" 
                                                value={formData.returnCarDate} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* AIRBNB SERVICE TYPE FIELDS */}
                            {(formData.serviceType === "AirBnB" || formData.serviceType === "Airbnb") && (
                                <>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-map-location-dot" />
                                        </div>
                                        <div className="search-input">
                                            <label>Accommodation Location</label>
                                            <NiceSelect
                                                options={accommodationLocations}
                                                defaultValue="Select Location"
                                                onChange={(value) => handleChange("accommodationLocation", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-home" />
                                        </div>
                                        <div className="search-input">
                                            <label>Accommodation Type</label>
                                            <NiceSelect
                                                options={accommodationTypes}
                                                defaultValue="Select Type"
                                                onChange={(value) => handleChange("accommodationType", value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Check-in Date</label>
                                            <input 
                                                type="date" 
                                                name="airbnbCheckIn" 
                                                value={formData.airbnbCheckIn} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-calendar-days" />
                                        </div>
                                        <div className="search-input">
                                            <label>Check-out Date</label>
                                            <input 
                                                type="date" 
                                                name="airbnbCheckOut" 
                                                value={formData.airbnbCheckOut} 
                                                onChange={handleDateChange}
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6 col-lg-auto">
                                        <div className="icon">
                                            <i className="fa-light fa-users" />
                                        </div>
                                        <div className="search-input">
                                            <label>Guests</label>
                                            <input 
                                                type="number" 
                                                name="guests" 
                                                value={formData.guests} 
                                                onChange={handleNumberChange}
                                                min="1" 
                                                max="20" 
                                                className="form-control" 
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Adults and Children counts - Show for all service types */}
                            <div className="form-group col-md-6 col-lg-auto">
                                <div className="icon">
                                    <i className="fa-light fa-user" />
                                </div>
                                <div className="search-input">
                                    <label>Adults</label>
                                    <input 
                                        type="number" 
                                        name="adults" 
                                        value={formData.adults} 
                                        onChange={handleNumberChange}
                                        min="1" 
                                        max="10" 
                                        className="form-control" 
                                    />
                                </div>
                            </div>
                            
                            <div className="form-group col-md-6 col-lg-auto">
                                <div className="icon">
                                    <i className="fa-light fa-child" />
                                </div>
                                <div className="search-input">
                                    <label>Children</label>
                                    <input 
                                        type="number" 
                                        name="children" 
                                        value={formData.children} 
                                        onChange={handleNumberChange}
                                        min="0" 
                                        max="10" 
                                        className="form-control" 
                                    />
                                </div>
                            </div>

                            {/* Submit Button with dynamic text */}
                            <div className="form-btn col-md-12 col-lg-auto">
                                <button className="th-btn" type="submit">
                                    <img src="/assets/img/icon/search.svg" alt="" />
                                    Search
                                </button>
                            </div>
                        </div>

                        {/* Form Message Display */}
                        {formMessage.text && (
                            <p className={`form-messages mb-0 mt-3 ${formMessage.type === "error" ? "text-danger" : "text-success"}`}>
                                {formMessage.text}
                            </p>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default Booking;
